import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Paper,
  Divider,
} from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import { toast } from "react-toastify";
import Seo from "./../components/Seo";

const PRIMARY_COLOR = "#1a237e";
const HOVER_COLOR = "#1a227eda";
const EMAIL = "info@burjalyarmouk.com";
const DOMAIN = "http://burjalyarmouk.com/";

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(false);
    setSubmitted(false);

    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          access_key: "de439868-536f-48aa-8379-c6d5b7602433", // 🔑 replace with your Web3Forms access key
          subject: `New Inquiry from ${form?.name} | Burj Al Yarmouk Industries LLC`,
          name: form.name,
          email: form.email,
          message: form.message,
        }),
      });

      const result = await response.json();

      if (result.success) {
        setSubmitted(true);
        // toast.success("✅ Your message has been sent successfully!");
        setForm({ name: "", email: "", message: "" });
        setTimeout(() => setSubmitted(false), 4000);
      } else {
        // toast.error("❌ Something went wrong. Please try again.");
        setError(true);
      }
    } catch (err) {
      // toast.error("❌ Something went wrong. Please try again.");
      console.error("Error submitting form:", err);
      setError(true);
    }

    setLoading(false);
  };

  return (
    <Box
      sx={{
        maxWidth: "1000px",
        mx: "auto",
        p: { xs: 3, md: 8 },
        minHeight: "80vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Seo
        title="Contact Us | Burj Al Yarmouk Industries LLC"
        description="Get in touch with Burj Al Yarmouk Industries LLC for engineering, fabrication, storage tanks, generator enclosures, steel structures, and industrial solutions in the UAE. Contact us today for quotes and support."
        url="https://burjalyarmouk.com/contact-us"
        image="https://burjalyarmouk.com/og-banner.jpg"
        keywords="contact Burj Al Yarmouk, fabrication company UAE contact, engineering services UAE, generator enclosure quote, steel fabrication UAE, Abu Dhabi industrial company"
        schema={{
          "@context": "https://schema.org",
          "@type": "ContactPage",
          name: "Contact Us - Burj Al Yarmouk Industries LLC",
          url: "https://burjalyarmouk.com/contact-us",
          description:
            "Reach out to Burj Al Yarmouk Industries LLC for inquiries related to fabrication, engineering, industrial products, and custom metal works.",
          mainEntity: {
            "@type": "Organization",
            name: "Burj Al Yarmouk Industries LLC",
            url: "https://burjalyarmouk.com/",
            logo: "https://burjalyarmouk.com/favicon.ico",
            sameAs: [
              "https://www.instagram.com/burjalyarmouk/",
              "https://www.linkedin.com/in/burj-al-yarmouk-industries-llc-417570347/",
            ],
            contactPoint: [
              {
                "@type": "ContactPoint",
                telephone: "+971508517296",
                contactType: "customer service",
                areaServed: "AE",
                availableLanguage: ["English", "Arabic"],
              },
            ],
            address: {
              "@type": "PostalAddress",
              addressLocality: "Abu Dhabi",
              addressCountry: "UAE",
            },
          },
        }}
      />

      <Paper
        elevation={6}
        sx={{
          p: { xs: 3, md: 6 },
          width: "100%",
          borderRadius: "12px",
          display: "flex",
          flexDirection: "column",
          gap: 4,
        }}
      >
        <Typography
          variant="h4"
          component="h1"
          sx={{
            fontWeight: 800,
            color: PRIMARY_COLOR,
            borderBottom: `3px solid ${PRIMARY_COLOR}`,
            display: "inline-block",
            pb: 1,
            mb: 1,
          }}
        >
          Get In Touch
        </Typography>
        <Typography sx={{ color: "text.primary", fontSize: "1.1rem" }}>
          We'd love to hear from you. Please fill out the form below and we'll
          get back to you as soon as possible.
        </Typography>

        {/* Success & Error Messages */}
        {submitted && (
          <Alert severity="success" sx={{ mb: 2 }}>
            ✅ Success! Your message has been sent successfully.
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            ❌ Something went wrong. Please try again later.
          </Alert>
        )}

        {/* Contact Form */}
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 4,
          }}
        >
          <TextField
            label="Your Name"
            variant="standard"
            name="name"
            value={form.name}
            onChange={handleChange}
            required
            fullWidth
          />
          <TextField
            label="Your Email"
            variant="standard"
            name="email"
            type="email"
            value={form.email}
            onChange={handleChange}
            required
            fullWidth
          />
          <TextField
            label="Your Message"
            variant="outlined"
            name="message"
            value={form.message}
            onChange={handleChange}
            multiline
            rows={5}
            required
            fullWidth
            sx={{ mt: 1 }}
          />
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            sx={{
              mt: 2,
              bgcolor: PRIMARY_COLOR,
              color: "white",
              "&:hover": { bgcolor: HOVER_COLOR },
              fontWeight: 600,
              borderRadius: "8px",
              py: 1.5,
              fontSize: "1rem",
            }}
          >
            {loading ? "Sending..." : "Send Message"}
          </Button>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Alternative Contact */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            color: "text.secondary",
          }}
        >
          <EmailIcon sx={{ color: PRIMARY_COLOR }} />
          <Typography sx={{ fontSize: "1rem" }}>
            Prefer to email directly? Reach us at:
            <a
              href={`mailto:${EMAIL}`}
              style={{
                color: PRIMARY_COLOR,
                fontWeight: 700,
                textDecoration: "none",
                marginLeft: "8px",
              }}
            >
              {EMAIL}
            </a>
          </Typography>
        </Box>

        <Typography sx={{ fontSize: "0.9rem", color: "text.disabled" }}>
          Visit our website:{" "}
          <a
            href={`https://${DOMAIN}`}
            style={{ color: PRIMARY_COLOR, textDecoration: "none" }}
          >
            {DOMAIN}
          </a>
        </Typography>
      </Paper>
    </Box>
  );
};

export default Contact;
